import { ComponentProps, forwardRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface ApiKeyInputProps extends Omit<ComponentProps<"input">, "id"> {
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  error?: boolean;
}

export const ApiKeyInput = forwardRef<HTMLInputElement, ApiKeyInputProps>(
  ({ label, value, onChange, placeholder, error, className, ...props }, ref) => {
    const inputId = label.toLowerCase().replace(/\s+/g, "-");
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    return (
      <div className="flex flex-col gap-2 w-full">
        <Label htmlFor={inputId} className="text-sm font-medium text-cede-text-primary">
          {label}
        </Label>
        <div
          className="relative bg-cede-bg-component border border-cede-border-container rounded-lg p-0.5"
          style={{ backdropFilter: "blur(20px)" }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Visual background layer with bluish tint */}
          <div
            className={cn(
              "absolute inset-0.5 rounded-md pointer-events-none transition-all duration-200",
              error
                ? "bg-cede-error/20"
                : isFocused
                  ? "bg-cede-primary/20"
                  : isHovered
                    ? "bg-cede-primary/12"
                    : value
                      ? "bg-cede-primary/15"
                      : "bg-cede-primary/8",
            )}
            style={{ boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.1)" }}
          />
          <input
            ref={ref}
            id={inputId}
            type={props.type || "text"}
            value={value}
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholder}
            autoComplete="off"
            // !ACHTUNG!: Don't remove this class. It's used to block the text input in Mixpanel.
            className={cn(
              "mp-block w-full h-[56px] px-4 py-4 bg-transparent border-none rounded-md",
              "text-cede-text-primary font-semibold text-base leading-[140%] tracking-[0.02em]",
              "placeholder:text-cede-text-secondary placeholder:font-normal",
              "focus:outline-none transition-all duration-200",
              "disabled:opacity-60 disabled:cursor-not-allowed relative",
              error && "text-cede-error",
              className,
            )}
            style={{ colorScheme: "dark" }}
            {...props}
          />
        </div>
      </div>
    );
  },
);

ApiKeyInput.displayName = "ApiKeyInput";
